import { useNavigate, useRouterState } from '@tanstack/react-router';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Focusable, Selection } from 'react-aria-components';

import { merge } from '@/components';
import { Flex, Heading, Menu, MenuItem, Save, Text, Tooltip, TooltipTrigger, TruncatedText } from '@/components/core';
import {
  ChatIcon,
  ChevronSelectorIcon,
  ExpertSessionIcon,
  InsightsIcon,
  OverflowIcon,
  RunsIcon,
  ShareFeedbackIcon,
  WarningDuotoneIcon,
  WorkflowIcon,
} from '@/components/icons';
import { NavigationBar as NavBar } from '@/components/ui';
import { DefaultPermissions } from '@/constants';
import {
  useAgentIcon,
  useDebounceCallback,
  useGetAccessLevel,
  useGetAgentFromPath,
  useGetNotConfiguredExports,
  useTour,
} from '@/hooks';
import { toAgentId, toModelId, useGetModel, useUpdateAgent } from '@/hooks/api';
import { AccessLevel, findModelId } from '@/utils';

import { AgentOptionsMenu } from './AgentOptionsMenu';
import { AgentSelectorMenu } from './AgentSelectorMenu';
import { UpgradeCard } from './UpgradeCard';
import { WorkspaceCard } from './WorkspaceCard';

const INavigationBar = () => {
  const { data: agent } = useGetAgentFromPath();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const { actions } = useGetNotConfiguredExports({ agentId: agent?.agentId });
  const { data: model } = useGetModel(toModelId(findModelId(agent)));
  const { showConfirmPredictionsTooltip } = useTour();

  const { hasAccessLevel, hasPermission } = useGetAccessLevel();

  const routeHandler = useMemo(() => {
    if (!agent) return;

    const runsRoute = { to: '/agents/$agentId/inbox', params: { agentId: agent.agentId } };
    const workflowRoute = { to: '/agents/$agentId/integration', params: { agentId: agent.agentId } };
    const insightsRoute = { to: '/agents/$agentId/analytics', params: { agentId: agent.agentId } };

    return {
      runs: {
        id: 'runs',
        navigate: async () => navigate(runsRoute),
        isSelected: routerState.location.pathname.endsWith('inbox'),
      },
      workflow: {
        id: 'workflow',
        navigate: async () => navigate(workflowRoute),
        isSelected: routerState.location.pathname.endsWith('integration'),
      },
      insights: {
        id: 'insights',
        navigate: async () => navigate(insightsRoute),
        isSelected: routerState.location.pathname.endsWith('analytics'),
      },
    };
  }, [navigate, agent, routerState.location.pathname]);

  const selectedKeysFromRouteHandler = useMemo(
    () =>
      Object.values(routeHandler ?? {})
        .filter((route) => route.isSelected)
        .map((route) => route.id),
    [routeHandler]
  );

  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set(selectedKeysFromRouteHandler));

  useEffect(() => {
    setSelectedKeys(new Set(selectedKeysFromRouteHandler));
  }, [selectedKeysFromRouteHandler]);

  const agentIcon = useAgentIcon({ agentId: agent?.agentId, className: 'size-3.5' });

  const [isRenaming, setIsRenaming] = useState(false);
  const [agentName, setAgentName] = useState(agent?.name ?? undefined);
  const agentRef = useRef(agent?.agentId);

  useEffect(() => {
    if (!agent) return;
    if (agent.agentId !== agentRef.current) {
      setAgentName(agent.name ?? undefined);
      agentRef.current = agent.agentId;
    }
  }, [agent]);

  const { mutateAsync: updateAgent } = useUpdateAgent();
  const onChangeAgentName = useDebounceCallback(
    useCallback(
      (name: string) => {
        const agentId = toAgentId(agent?.agentId);
        if (agentId) {
          updateAgent({ id: agentId, updates: { name } });
        }
      },
      [agent?.agentId, updateAgent]
    ),
    200
  );

  useEffect(() => {
    if (agentName) {
      onChangeAgentName(agentName);
    }
  }, [agentName, onChangeAgentName]);

  const inputRef = useRef<HTMLInputElement>(null);

  const onRename = useCallback(() => {
    setIsRenaming(true);
    setTimeout(() => inputRef.current?.focus(), 200);
  }, []);

  if (!agent || !routeHandler) {
    return <Save message="Loading agent ..." />;
  }

  const flowSetupRequired =
    !showConfirmPredictionsTooltip && (actions.length || Object.entries(model?.fieldConfig ?? {}).length < 1);

  const canCreateAgents = hasPermission(DefaultPermissions.AllAgentsWrite);

  return (
    <NavBar>
      <WorkspaceCard className="h-12 border-b-1 border-gray-200" organizationId="me" />

      <div className="flex flex-col gap-2 p-2">
        <Flex variant="secondary" className="group/agent-card gap-sm items-center rounded-lg pr-1 pl-2 shadow-xs">
          {agentIcon}

          {isRenaming ? (
            <input
              ref={inputRef}
              autoFocus
              className="font-inter border-none py-0.75 text-sm font-medium outline-none"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              onBlur={() => setIsRenaming(false)}
            />
          ) : (
            <>
              <TruncatedText onClick={() => setIsRenaming(true)} className="font-medium">
                {agent.name}
              </TruncatedText>

              <div className="grow" />

              <div className="flex items-center">
                {canCreateAgents && (
                  <AgentOptionsMenu className="invisible !p-1 group-hover/agent-card:visible" onRename={onRename}>
                    <OverflowIcon />
                  </AgentOptionsMenu>
                )}

                <AgentSelectorMenu className="!p-1">
                  <ChevronSelectorIcon />
                </AgentSelectorMenu>
              </div>
            </>
          )}
        </Flex>

        <Menu
          className={merge('gap-xs flex flex-col', isRenaming && 'pointer-events-none')}
          selectionMode="single"
          selectedKeys={selectedKeys}
          aria-label="Main"
        >
          <MenuItem id={routeHandler.runs.id} onAction={routeHandler.runs.navigate} aria-label="Runs">
            <RunsIcon />
            <Text>Runs</Text>
          </MenuItem>

          {hasAccessLevel(AccessLevel.Developer) && (
            <MenuItem id={routeHandler.workflow.id} onAction={routeHandler.workflow.navigate} aria-label="Workflow">
              <WorkflowIcon />
              <Text>Workflow</Text>
              {flowSetupRequired ? (
                <>
                  <div className="grow" />
                  <TooltipTrigger>
                    <Tooltip className="flex flex-col" variant="primary">
                      <Heading>Setup needed</Heading>
                      <Text>Configure your integration to export files automatically.</Text>
                    </Tooltip>
                    <Focusable>
                      <WarningDuotoneIcon role="button" variant="warning" />
                    </Focusable>
                  </TooltipTrigger>
                </>
              ) : null}
            </MenuItem>
          )}

          <MenuItem id={routeHandler.insights.id} onAction={routeHandler.insights.navigate} aria-label="Insights">
            <InsightsIcon />
            <Text>Insights</Text>
          </MenuItem>
        </Menu>
      </div>

      <div className="grow" />

      <div className="p-2">
        <Menu>
          <MenuItem onAction={() => window.open('https://tally.so/r/3qkeN5', '_blank')}>
            <ShareFeedbackIcon />
            <Text>Share feedback</Text>
          </MenuItem>
          <MenuItem onAction={() => window.open('https://cal.com/cradlai/30min', '_blank')}>
            <ExpertSessionIcon />
            <Text>Book free expert session</Text>
          </MenuItem>
          <MenuItem onAction={() => window._chatlio?.open()}>
            <ChatIcon />
            <Text>Chat with an expert</Text>
          </MenuItem>
        </Menu>
      </div>

      <UpgradeCard className="m-4" organizationId="me" />

      <Text className="mb-2 self-end px-4 text-gray-300">
        version: {(import.meta.env.VITE_CURRENT_COMMIT_SHA ?? '').substring(0, 7) || 'dev'}
      </Text>
    </NavBar>
  );
};

export const NavigationBar = memo(INavigationBar);
